/**
 * Format a phone number to E.164 format
 * E.164 format: +[country code][number] (e.g., +12345678900)
 * 
 * @param {string} phoneNumber - The phone number to format
 * @param {string} defaultCountryCode - Default country code (default: '1' for US/Canada)
 * @returns {string|null} - Formatted phone number in E.164 format or null if invalid
 */
export function formatPhoneToE164(phoneNumber, defaultCountryCode = '1') {
	if (!phoneNumber) return null

	// Remove all non-digit characters
	const cleaned = phoneNumber.replace(/\D/g, '')

	// If empty after cleaning, return null
	if (!cleaned) return null

	// If it starts with country code and has correct length
	// US/Canada: 11 digits (1 + 10 digits)
	if (cleaned.length === 11 && cleaned.startsWith('1')) {
		return `+${cleaned}`
	}

	// If it's 10 digits, add default country code
	if (cleaned.length === 10) {
		return `+${defaultCountryCode}${cleaned}`
	}

	// If it already starts with +, validate and return
	if (phoneNumber.startsWith('+')) {
		// Basic validation: should have at least 10 digits after +
		if (cleaned.length >= 10) {
			return `+${cleaned}`
		}
	}

	// If it's longer than 11 digits, assume it already has country code
	if (cleaned.length > 11) {
		return `+${cleaned}`
	}

	// Default: add country code
	if (cleaned.length >= 10) {
		return `+${defaultCountryCode}${cleaned}`
	}

	// Invalid format
	return null
}

/**
 * Validate if a phone number is valid for E.164 format
 * 
 * @param {string} phoneNumber - The phone number to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export function isValidPhoneNumber(phoneNumber) {
	if (!phoneNumber) return false

	// Remove all non-digit characters
	const cleaned = phoneNumber.replace(/\D/g, '')

	// Must have at least 10 digits (minimum valid phone number)
	// and no more than 15 digits (E.164 max length)
	return cleaned.length >= 10 && cleaned.length <= 15
}

/**
 * Format phone number for display (US format)
 * 
 * @param {string} phoneNumber - The phone number to format
 * @returns {string} - Formatted phone number for display (e.g., (123) 456-7890)
 */
export function formatPhoneForDisplay(phoneNumber) {
	if (!phoneNumber) return ''

	// Remove all non-digit characters
	const cleaned = phoneNumber.replace(/\D/g, '')

	// US/Canada format (10 digits)
	if (cleaned.length === 10) {
		return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
	}

	// US/Canada with country code (11 digits)
	if (cleaned.length === 11 && cleaned.startsWith('1')) {
		return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
	}

	// For other formats, just return cleaned or original
	return phoneNumber
}

